import React, { useMemo } from "react";

import {
    Alert,
    Button,
    CircularProgress,
    Stack,
    Typography,
} from "@mui/material";

import DynamicFieldTable from "components/organisms/DynamicFieldTable";

import {
    areGoogleAdsMappingsValid,
    getConversionLabel,
    getConversionsLoadErrorDisplay,
    getEmptyConversionsMessage,
} from "./GoogleAdsConversion.helpers";

const EMPTY_MAPPING = { conversion_id: "", event: "", conversion_name: "" };
const CUSTOM_EVENT_VALUE = "__custom_event__";
const CUSTOM_EVENT_OPTION = {
    label: "Custom event",
    value: CUSTOM_EVENT_VALUE,
};

const GoogleAdsConversionMappingContent = ({
    type,
    conversions,
    events,
    selectedMappings,
    setSelectedMappings,
    loadError,
    onRetry,
    isLoading,
}) => {
    const conversionOptions = useMemo(
        () =>
            (conversions ?? []).map((conversion) => ({
                label: getConversionLabel(conversion),
                value: conversion?.id?.toString(),
            })),
        [conversions],
    );

    const rows = useMemo(
        () =>
            selectedMappings?.length
                ? selectedMappings
                : [{ ...EMPTY_MAPPING }],
        [selectedMappings],
    );

    const transformedFields = useMemo(() => {
        const usedEvents = rows.map((mapping) => mapping.event);
        const usedConversionIds = rows.map((mapping) =>
            mapping.conversion_id?.toString(),
        );
        return rows.map((mapping) => ({
            event: {
                value: mapping.event || "",
                variant: mapping.isCustom ? "TEXT" : "AUTOCOMPLETE",
                autoFocus: mapping.focusEventInput === true,
                placeholder: mapping.isCustom
                    ? "Enter an event"
                    : "Select an event",
                options: [
                    ...(events ?? [])
                        .filter(
                            (event) =>
                                !event.isCustom &&
                                (event.code === mapping.event ||
                                    !usedEvents.includes(event.code)),
                        )
                        .map((event) => ({
                            label: event.name,
                            value: event.code,
                        })),
                    CUSTOM_EVENT_OPTION,
                ],
            },
            conversion_id: {
                value: mapping.conversion_id || "",
                options: conversionOptions.filter(
                    (conversion) =>
                        conversion.value === mapping.conversion_id ||
                        !usedConversionIds.includes(conversion.value),
                ),
            },
        }));
    }, [rows, events, conversionOptions]);

    const handleFieldsChange = (newFields) => {
        setSelectedMappings(
            newFields.map((field) => {
                const conversionId =
                    field.conversion_id?.value?.toString().trim() || "";
                const conversion = (conversions ?? []).find(
                    (item) => item?.id?.toString() === conversionId?.toString(),
                );
                const selectedEvent =
                    field.event?.value?.toString().trim() || "";
                const isCustomOption = selectedEvent === CUSTOM_EVENT_VALUE;
                const event = (events ?? []).find(
                    (item) => item?.code?.toString() === selectedEvent,
                );
                const isCustom =
                    field.event?.variant === "TEXT" ||
                    isCustomOption ||
                    event?.isCustom === true;

                return {
                    conversion_id: conversionId,
                    event: isCustomOption ? "" : selectedEvent,
                    isCustom,
                    focusEventInput:
                        isCustomOption || field.event?.autoFocus === true,
                    conversion_name: conversion
                        ? (conversion.name ?? conversion.conversion_name ?? "")
                        : "",
                };
            }),
        );
    };

    const fieldsConfiguration = [
        {
            name: "event",
            label: "Internal Event",
            variant: "AUTOCOMPLETE",
            placeholder: "Select an event",
        },
        {
            name: "conversion_id",
            label: "Google Ads Conversion",
            variant: "AUTOCOMPLETE",
            placeholder: "Select Conversion",
        },
    ];

    const normalizedEvents = rows
        .map((mapping) => String(mapping.event ?? "").trim())
        .filter(Boolean);
    const hasDuplicateEvent =
        (type === "ggalc" || type === "ggass") &&
        new Set(normalizedEvents).size !== normalizedEvents.length;
    const hasInvalidMapping = !areGoogleAdsMappingsValid(
        rows,
        conversions,
        type,
    );

    if (isLoading) {
        return (
            <Stack alignItems="center" justifyContent="center" py={4}>
                <CircularProgress />
            </Stack>
        );
    }

    if (loadError) {
        return (
            <Alert
                severity="error"
                action={
                    onRetry && (
                        <Button
                            color="error"
                            variant="contained"
                            size="small"
                            onClick={onRetry}
                        >
                            Retry
                        </Button>
                    )
                }
            >
                {getConversionsLoadErrorDisplay(loadError)}
            </Alert>
        );
    }

    if (!conversions?.length) {
        return (
            <Alert severity="info">{getEmptyConversionsMessage(type)}</Alert>
        );
    }

    return (
        <Stack spacing={2}>
            <p>
                Map your internal events to Google Ads conversion actions. Each
                event can be mapped only once and at least one mapping is
                required. Custom events are supported.
            </p>

            <DynamicFieldTable
                isScrollable={false}
                maxHeight={null}
                fields={transformedFields}
                setFields={handleFieldsChange}
                fieldsConfiguration={fieldsConfiguration}
                minLength={1}
                addMoreDisabled={false}
                addMoreTooltip=""
                showStatus={false}
            />
            {hasInvalidMapping && (
                <Alert severity="warning">
                    {hasDuplicateEvent
                        ? "Each event can be mapped only once."
                        : "Select an event and Google Ads conversion for every row."}
                </Alert>
            )}
        </Stack>
    );
};

export default GoogleAdsConversionMappingContent;
